import type { ItemConfig, ProdFam, ProdType, ScreenItem } from '@alwindows/shared/api/screens'

/**
 * The in-flight screen-quote shape. Persisted to sessionStorage so a refresh
 * mid-flow doesn't wipe progress (per spec — screens has no persistent cart,
 * but the multi-page configurator can take a while).
 */

export const QUOTE_STORAGE_KEY = 'al:screens:quote:v1'
// v2: ScreenLine gained roomName + cached sub-option prices (meshPrice etc).
// v3: cached option/sub-option descriptions for the Summary review list.
// Quotes persisted at older versions would restore with configured lines
// missing those fields (wrong totals / blank customisations) — discard them.
export const QUOTE_VERSION = 3 as const

export type Customer = {
  firstName: string
  lastName: string
  email: string
  phone: string
  street: string
  city: string
  state: string
  postcode: string
  addrNo: string
  /**
   * Lot number for display, when the property was identified by one (SAP
   * LotNo, or a manual lot search). Optional — absent for plain street-number
   * addresses and for quotes persisted before this field existed.
   */
  lot?: string
  salesOrg: string
}

/**
 * Configuration captured for a single screen line. References the source
 * SO/Item from /v1/screen/items so submission can rebuild the SAP payload.
 */
export type ScreenLine = {
  sourceItem: ScreenItem
  /** Snapshot of the original ItemToItemConfig so the submit payload can rebuild it. */
  sourceConfig: ItemConfig[]
  selected: boolean
  qty: number
  /**
   * Customer-editable label for the opening ("Kitchen window"), legacy
   * RoomName. Seeded with the product display name; sent to SAP in
   * ToScrItm[].RoomName so staff/installers can identify which opening each
   * screen belongs to. Quotes persisted before this field exists may lack
   * it — read sites fall back to lineDisplayName().
   */
  roomName: string
  /** Frame colour ColourCode from /v1/screen/colours. */
  frameColour: string
  /**
   * Human name of the frame colour ("White"). Seeded from FM_ALU_COLOUR's
   * Value, updated when a swatch is picked. Legacy sent the NAME in the
   * Hana payload's FrameColor/DefaultFrameColor (the code only goes in the
   * Idoc ColourCode) — without this, staff reviewing the HANA quote see
   * raw codes like "#FFFFFF".
   */
  frameColourDesc: string
  balRating: string
  /** ProdType of the picked option from /v1/screen/options.results (e.g. "BR" Barrier). */
  screenOption: string
  /** Selected sub-options (only one of each is picked per the legacy UX). */
  meshType: string
  grillType: string
  petFlapType: string
  hasTripleLock: boolean
  /** Cached price + descriptive fields, copied from the picked option for the totals row. */
  unitPrice: number
  fitCost: number
  tripleLockCost: number
  nonTradeCharge: number
  /**
   * Sub-option prices, cached from the picked screen option's nested
   * ScreenToMesh/Grill/PetFlap rows when the selection is made. Without
   * these the line total (and the SAP payload) silently drops the cost of
   * the chosen mesh/grill/pet flap.
   */
  meshPrice: number
  meshNonTrade: number
  grillPrice: number
  grillNonTrade: number
  petFlapPrice: number
  petFlapNonTrade: number
  /**
   * Human-readable descriptions of the picked option + sub-options, cached
   * alongside the prices. The Summary review list shows these (live-site
   * parity: "Fly screen / Aluminium Mesh"), not the SAP ProdType codes.
   */
  screenOptionDesc: string
  meshDesc: string
  grillDesc: string
  petFlapDesc: string
  /**
   * The picked sub-option's own ProdFam, cached alongside its price/desc.
   * Legacy pushed the whole option row — including ProdFam — into the SAP
   * ToMesh/ToGrill/ToPetflap payload (Editor.onOptionChange → Helper.js), so
   * we carry it too. Payload-only passthrough: it affects neither the line
   * total nor any display, so quotes persisted before this field existed
   * restore fine (Submit falls back to '').
   */
  meshProdFam: string
  grillProdFam: string
  petFlapProdFam: string
}

export type Quote = {
  version: typeof QUOTE_VERSION
  customer: Customer | null
  salesOrderNo: string | null
  lines: ScreenLine[]
  promoCode: string
  /**
   * Applied promo discount in dollars, set when Apply succeeds on Summary.
   * Persisted on the quote (NOT router state) so a refresh of /submit can't
   * silently send PromoCode with PromoAmount 0.
   */
  promoAmount: number
  installCost: number
  refNo: string | null
  /** True once the user has chosen a sales order and we've populated lines from /v1/screen/items. */
  itemsLoaded: boolean
  /**
   * Free-text notes from the customer, surfaced in the SAP HanaPayload as
   * `Comments`. Capped to 500 chars in the UI; legacy parity. Support staff
   * use this to disambiguate custom dimensions / install notes / etc.
   */
  comments: string
}

export const EMPTY_QUOTE: Quote = {
  version: QUOTE_VERSION,
  customer: null,
  salesOrderNo: null,
  lines: [],
  promoCode: '',
  promoAmount: 0,
  installCost: 0,
  refNo: null,
  itemsLoaded: false,
  comments: '',
}

/** Helpers for pulling characteristic values out of ItemToItemConfig. */
export function getConfigValue(config: ItemConfig[], name: string): string {
  return config.find((c) => c.Name === name)?.Value ?? ''
}

export function getConfigCharacteristic(config: ItemConfig[], name: string): string {
  return config.find((c) => c.Name === name)?.Characteristic ?? ''
}

/**
 * Saved-line price, matching legacy Editor.calculateTotal exactly:
 * (base + fit + non-trade + mesh + grill) × qty, then pet flap and triple
 * lock added ONCE per line (legacy applied those after the qty multiply).
 * qty isn't user-editable — each line is an individual window/door;
 * Quantity comes from the original order line. The `|| 0` guards cover
 * quotes persisted before the sub-option price fields existed.
 */
export function lineTotal(line: ScreenLine): number {
  // `n()` coerces any missing/garbage field to 0 so a single bad value
  // can't turn the whole cart total into "$NaN" (e.g. a partially-shaped
  // quote that slipped a version guard).
  const n = (v: number) => (Number.isFinite(v) ? v : 0)
  const perUnit =
    n(line.unitPrice) +
    n(line.fitCost) +
    n(line.nonTradeCharge) +
    n(line.meshPrice) +
    n(line.meshNonTrade) +
    n(line.grillPrice) +
    n(line.grillNonTrade)
  let total = perUnit * n(line.qty || 1)
  total += n(line.petFlapPrice) + n(line.petFlapNonTrade)
  if (line.hasTripleLock) total += n(line.tripleLockCost)
  return total
}

/**
 * Product name for a line — what KIND of opening this is. Legacy parity:
 * the display name comes from the PROD_FAM characteristic's Value ("Window
 * Sliding", "Door Hinged"). The item-level ProdDesc is NOT a product name —
 * SAP sends the opening's order tag there ("W01", "TESTROOM"), which seeds
 * the customer-editable roomName instead.
 */
export function lineDisplayName(line: ScreenLine): string {
  return (
    getConfigValue(line.sourceConfig, 'PROD_FAM') ||
    line.sourceItem.ProdDesc ||
    (line.sourceItem.ProdType === 'DR' ? 'Door' : 'Window')
  )
}

/**
 * Screen type with no frame-colour choice — retractable insect screens, whose
 * picker legacy hid entirely (Editor.js). Their line still carries the home's
 * seeded colour, so the Summary must not present it as a customer choice.
 * Lives here rather than in LineConfigurator because both the picker and the
 * review list have to agree on it.
 */
export const RETRACTABLE_DOOR = 'RET'

export type LineAttribute = { label: string; value: string }

/**
 * The customisation rows shown under an item on the Summary review — every
 * choice the configurator offered for this line: screen type, mesh, grill,
 * pet flap, triple lock, frame colour. Legacy listed these unlabelled
 * (Editor.js `attributes`); labelling them makes the "please check the screen
 * details indicated are correct" step actually checkable.
 *
 * `homeColourName` is the order's frame colour ("Jasper"), used when the line
 * has no cached colour name of its own — SAP only reliably carries
 * FM_ALU_COLOUR's Value on the FIRST item, the same quirk the configurator
 * works around when pre-selecting a swatch. Values fall back to the raw
 * ProdType code for quotes saved before the descriptions were cached.
 */
export function lineAttributes(line: ScreenLine, homeColourName = ''): LineAttribute[] {
  const out: LineAttribute[] = []
  const push = (label: string, value: string) => {
    if (value) out.push({ label, value })
  }
  if (line.screenOption) push('Screen type', line.screenOptionDesc || line.screenOption)
  if (line.meshType) push('Insect mesh', line.meshDesc || line.meshType)
  if (line.grillType) push('Grill', line.grillDesc || line.grillType)
  if (line.petFlapType) push('Pet flap', line.petFlapDesc || line.petFlapType)
  if (line.hasTripleLock) push('Triple lock', 'Yes')
  // Frame colour is picked per line, so a line may differ from the order's
  // header colour — show what THIS opening will actually be made in. Never
  // print the raw code (`WH`) as a fallback: with no name for it, the header
  // colour is the honest answer.
  if (line.screenOption !== RETRACTABLE_DOOR)
    push('Frame colour', line.frameColourDesc || homeColourName)
  return out
}

/**
 * Product families whose presence on the order DOUBLES the installation fee
 * (legacy AppConstants INSTALL_DOUBLE_FAM — hinged/French door families need
 * a two-person install).
 */
export const INSTALL_DOUBLE_FAM: readonly string[] = ['DHG', 'DFR', 'ETS', 'EAS']

/**
 * Installation charge for the quote as it stands. `quote.installCost` holds
 * the BASE per-order fee from /v1/screen/installCost; the charge applies
 * once at least one line is actually configured (legacy only set it in
 * redux when the first screen was chosen), and doubles while any configured
 * line belongs to an INSTALL_DOUBLE_FAM family. Derived on the fly — the
 * legacy app mutated a flagged value (×2 on add, ÷2 on cancel), which this
 * reproduces without the ordering bugs.
 */
export function effectiveInstallCost(quote: Quote): number {
  const configured = quote.lines.filter((l) => l.selected && l.screenOption)
  if (configured.length === 0) return 0
  const doubled = configured.some((l) =>
    INSTALL_DOUBLE_FAM.includes(prodFamFromConfig(l.sourceConfig)),
  )
  return quote.installCost * (doubled ? 2 : 1)
}

/** Read the ProdFam characteristic with TS narrowing for downstream calls. */
export function prodFamFromConfig(config: ItemConfig[]): ProdFam {
  return getConfigCharacteristic(config, 'PROD_FAM') as ProdFam
}

export function prodTypeFromItem(item: ScreenItem): ProdType {
  return item.ProdType
}
